var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useReducer, useState, useCallback, } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { addManageUsersApi } from '../api/b2b/add-manage-user';
import { addUserValidationSchema, } from '../components/user-form/manage-user-form/steps/validation';
import { userStepReducer, initialState, editModeInitialState, } from '../reducers/add-user-reducer';
export var ManageUserFormContext = React.createContext(null);
var ManageUserFormProvider = function (_a) {
    var children = _a.children, isEditMode = _a.isEditMode, hasProTradeCredit = _a.hasProTradeCredit, editableUser = _a.editableUser;
    var _b = useState(null), PTCs = _b[0], setPTCs = _b[1];
    var _c = useState(null), PTCsForUser = _c[0], setPTCsForUser = _c[1];
    var _d = useReducer(userStepReducer, isEditMode ? editModeInitialState : initialState), steps = _d[0], dispatch = _d[1];
    var _e = useState(null), proTradeCreditError = _e[0], setProTradeCreditError = _e[1];
    var isEditableUserEligibleForPTC = (editableUser === null || editableUser === void 0 ? void 0 : editableUser.role.toUpperCase()) === 'ADMIN' ||
        (editableUser === null || editableUser === void 0 ? void 0 : editableUser.role.toUpperCase()) === 'PURCHASER';
    var setDefaultValues = function () { return ({
        firstName: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.firstName) || '',
        lastName: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.lastName) || '',
        email: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.email) || '',
        role: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.role) || null,
        // paymentPermissions is not part of the User retrieval call
        // it is separately fetched from the "Retrieve Authorized Tenders by User" API
        paymentPermissions: [],
    }); };
    var formMethods = useForm({
        resolver: yupResolver(addUserValidationSchema),
        mode: 'onTouched',
        defaultValues: isEditMode
            ? setDefaultValues()
            : {
                firstName: '',
                lastName: '',
                email: '',
                role: null,
                paymentPermissions: [],
            },
    });
    var setValue = formMethods.setValue, watch = formMethods.watch;
    var currentlySelectedRole = watch('role');
    var assignPaymentPermissions = useCallback(function (paymentIds) {
        setValue('paymentPermissions', paymentIds);
    }, [setValue]);
    var handleUserTypeChange = function (newUserType) {
        var isChangingToRunner = newUserType === 'Runner';
        var isChangingFromRunner = currentlySelectedRole === 'Runner';
        if (isChangingToRunner) {
            // If the user type is changed to Runner, clear the payment permissions
            dispatch({ type: 'HIDE', target: 'PAYMENT_PERMISSIONS' });
            assignPaymentPermissions([]);
            return;
        }
        if (PTCs && isChangingFromRunner) {
            // If user type changes from Runner to something else,
            // show the payment permissions step and auto-populate the payment permissions
            dispatch({ type: 'SHOW', target: 'PAYMENT_PERMISSIONS' });
            // Default to current user’s PTCs if editing; otherwise, use full PTCs list
            var paymentPermissions = isEditMode ? PTCsForUser || [] : PTCs;
            // If the user was a Runner, assign all PTCs by default
            if ((editableUser === null || editableUser === void 0 ? void 0 : editableUser.role.toUpperCase()) === 'RUNNER') {
                paymentPermissions = PTCs;
            }
            assignPaymentPermissions(paymentPermissions.map(function (card) { return card.paymentId; }));
        }
        // No action needed when switching between non-Runner roles (e.g., Purchaser <-> Admin)
    };
    var handlePTCFetching = useCallback(function (allPTCs, userPTCs) {
        var editType = userPTCs ? 'COMPLETE' : 'HIDE';
        var type = isEditMode ? editType : 'SHOW';
        dispatch({
            type: type,
            target: 'PAYMENT_PERMISSIONS',
        });
        var paymentPermissions = isEditMode ? userPTCs || [] : allPTCs;
        assignPaymentPermissions(paymentPermissions.map(function (card) { return card.paymentId; }));
    }, [assignPaymentPermissions, isEditMode]);
    useEffect(function () {
        var ignoreRequest = false;
        if (hasProTradeCredit) {
            Promise.all([
                addManageUsersApi.getPTC(),
                isEditMode && (editableUser === null || editableUser === void 0 ? void 0 : editableUser.userId) && isEditableUserEligibleForPTC
                    ? addManageUsersApi.getPTCByUserId(editableUser.userId)
                    : // resolve as null if not in edit mode or no userId or not eligible
                        Promise.resolve(null),
            ])
                .then(function (_a) {
                var cards = _a[0], userCards = _a[1];
                if (ignoreRequest)
                    return;
                setPTCs(cards);
                setPTCsForUser(userCards);
                handlePTCFetching(cards, userCards);
            })
                .catch(function (error) {
                if (!ignoreRequest) {
                    setProTradeCreditError(error);
                }
            });
        }
        return function () {
            ignoreRequest = true;
        };
    }, [
        editableUser === null || editableUser === void 0 ? void 0 : editableUser.userId,
        hasProTradeCredit,
        isEditMode,
        isEditableUserEligibleForPTC,
        handlePTCFetching,
    ]);
    return (React.createElement(ManageUserFormContext.Provider, { value: {
            PTCs: PTCs,
            PTCsForUser: PTCsForUser,
            steps: steps,
            formMethods: formMethods,
            editableUser: editableUser,
            hasProTradeCredit: hasProTradeCredit,
            proTradeCreditError: proTradeCreditError,
            dispatch: dispatch,
            handleUserTypeChange: handleUserTypeChange,
        } },
        React.createElement(FormProvider, __assign({}, formMethods), children)));
};
export default ManageUserFormProvider;
